import {
  QueryClient,
  queryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { TransactionDetailResponse } from 'src/models/transaction.models'
import {
  getAdditionalFields,
  GetAdditionalFieldsResponse,
} from 'src/queries/organizations/additionalFields.network'
import {
  ADDITIONAL_FIELDS_QK,
  orgQueryKeys,
  transactionsQueryKeys,
} from 'src/queries/queries.constants'
import {
  ExtendBaseQueryOptions,
  getListQueryKeys,
  MutationMethod,
  useExtendMutation,
  UseExtendMutationVariablesBody,
} from 'src/queries/queries.utils'
import { updateTransactionDetailResponse } from 'src/queries/transactions.queries'

export interface AdditionalFieldsResponse {
  id: string
  name: string
  purpose?: 'NOTES' | 'CUSTOM'
  active: boolean
  required: boolean
  editable: boolean
  mccGroups?: string[] | null
}

export type AdditionalFieldsFiltersRequestParams = {
  active?: boolean
  required?: boolean
  sortField?: string
  sortDirection?: 'ASC' | 'DESC'
}

export const activeAlphabeticalAdditionalFieldsFiltersRequestParams: AdditionalFieldsFiltersRequestParams =
  {
    active: true,
    sortField: 'name',
    sortDirection: 'ASC',
  }

interface QueryAdditionalFieldsOptions
  extends ExtendBaseQueryOptions<
    { expenseAdditionalFields: AdditionalFieldsResponse[] },
    AdditionalFieldsResponse[]
  > {
  filters: AdditionalFieldsFiltersRequestParams
  additionalKey?: string
}

// Applies the backward-compatible `editable` default until all environments
// return the field. Lives in the queryFn, not a hook-level select, so the
// default travels with the cached data — prefetch / ensureQueryData /
// getQueryData consumers bypass select entirely.
const getDefaultedAdditionalFields = async (
  filters: AdditionalFieldsFiltersRequestParams,
): Promise<GetAdditionalFieldsResponse> => {
  const data = await getAdditionalFields(filters)
  return {
    expenseAdditionalFields: data.expenseAdditionalFields.map((field) => ({
      ...field,
      editable: field.editable ?? true,
    })),
  }
}

export const additionalFieldsQueries = {
  list: (
    filters: AdditionalFieldsFiltersRequestParams,
    additionalKey?: string,
  ) =>
    queryOptions({
      queryKey: orgQueryKeys.additionalFields(filters, additionalKey),
      queryFn: () => getDefaultedAdditionalFields(filters),
    }),
}

export const useQueryAdditionalFields = ({
  filters,
  additionalKey,
  queryCacheOptions,
  ...rest
}: QueryAdditionalFieldsOptions) => {
  return useQuery({
    ...additionalFieldsQueries.list(filters, additionalKey),
    select: (data: GetAdditionalFieldsResponse) => data.expenseAdditionalFields,
    ...queryCacheOptions,
    ...rest,
  })
}

export type PostAdditionalFieldRequest = {
  name: string
  active?: boolean
  required?: boolean
}

export const usePostAdditionalField = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    AdditionalFieldsResponse,
    UseExtendMutationVariablesBody<PostAdditionalFieldRequest>
  >(MutationMethod.POST, {
    mutationUrl: () => `/expensedata/additionalfields`,
    onSuccess: () => {
      // update list responses
      queryClient.invalidateQueries({
        queryKey: [ADDITIONAL_FIELDS_QK],
      })
    },
  })
}

export type PatchAdditionalFieldRequest = {
  name?: string
  active?: boolean
  required?: boolean
}

export interface PatchAdditionalFieldVariables {
  id: string
  body: PatchAdditionalFieldRequest
}

// Apply a per-field update across every cached additional-fields list, so the
// field doesn't jump around in any list view that was previously fetched with
// different filters.
const updateAdditionalFieldInLists = (
  queryClient: QueryClient,
  fieldId: string,
  merge: (field: AdditionalFieldsResponse) => AdditionalFieldsResponse,
): void => {
  getListQueryKeys(queryClient, [ADDITIONAL_FIELDS_QK]).forEach((queryKey) => {
    queryClient.setQueryData(
      queryKey,
      (oldFields: { expenseAdditionalFields: AdditionalFieldsResponse[] }) => {
        if (oldFields === undefined) return undefined
        return {
          expenseAdditionalFields: oldFields.expenseAdditionalFields.map(
            (field) => (field.id === fieldId ? merge(field) : field),
          ),
        }
      },
    )
  })
}

export const usePatchAdditionalField = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    AdditionalFieldsResponse,
    PatchAdditionalFieldVariables
  >(MutationMethod.PATCH, {
    mutationUrl: ({ id }) => `/expensedata/additionalfields/${id}`,
    onSuccess: (data) => {
      updateAdditionalFieldInLists(queryClient, data.id, (field) => ({
        ...field,
        ...data,
        // PATCH response may omit these — keep the cached value if so.
        editable: data.editable ?? field.editable,
        mccGroups: data.mccGroups ?? field.mccGroups,
      }))
    },
  })
}

export interface PatchCustomFieldMccGroupsVariables {
  customFieldId: string
  body: { mccGroups: string[] }
}

export const usePatchCustomFieldMccGroups = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    AdditionalFieldsResponse,
    PatchCustomFieldMccGroupsVariables
  >(MutationMethod.PATCH, {
    mutationUrl: ({ customFieldId }) =>
      `/expensedata/categories/${customFieldId}/mccgroups`,
    // Optimistic update so the checkbox flips immediately. onError below
    // rolls back by refetching authoritative state from the server.
    onMutate: ({ customFieldId, body }) => {
      updateAdditionalFieldInLists(queryClient, customFieldId, (field) => ({
        ...field,
        mccGroups: body.mccGroups,
      }))
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: [ADDITIONAL_FIELDS_QK] })
    },
    onSuccess: (data, variables) => {
      // Reconcile with server response. Defensive fallback to the value we
      // sent — backend should always return mccGroups, but we don't want
      // to wipe the user's selection if it ever omits the field.
      updateAdditionalFieldInLists(queryClient, data.id, (field) => ({
        ...field,
        mccGroups: data.mccGroups ?? variables.body.mccGroups,
      }))
    },
  })
}

export interface AdditionalFieldsTransactionResponse {
  id: string
  expenseAdditionalFieldId: string
  details?: string
}

export interface AdditionalField {
  expenseAdditionalFieldId: string
  details: string | undefined
}

export interface PutTransactionAdditionalFieldsRequest {
  additionalFields: AdditionalField[]
}

export interface PutTransactionAdditionalFieldsVariables {
  transactionId: string
  body: PutTransactionAdditionalFieldsRequest
}

export const usePutTransactionAdditionalFields = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    TransactionDetailResponse,
    PutTransactionAdditionalFieldsVariables
  >(MutationMethod.PUT, {
    mutationKey: transactionsQueryKeys.putAdditionalFields(),
    mutationUrl: ({ transactionId }) =>
      `/transactions/${transactionId}/additionalfields`,
    onSuccess: (data, variables) => {
      updateTransactionDetailResponse(queryClient, {
        transactionId: variables.transactionId,
        updater: (old) => ({
          ...old,
          additionalFields: data.additionalFields,
        }),
      })
    },
  })
}

export interface PatchSingleTransactionAdditionalFieldVariables {
  transactionId: string
  fieldId: string
  body: AdditionalField
}

export const usePatchSingleTransactionAdditionalField = () => {
  const queryClient = useQueryClient()
  return useExtendMutation<
    TransactionDetailResponse,
    PatchSingleTransactionAdditionalFieldVariables
  >(MutationMethod.PATCH, {
    mutationKey: transactionsQueryKeys.patchSingleAdditionalField(),
    mutationUrl: ({ transactionId, fieldId }) =>
      `/transactions/${transactionId}/additionalfields/${fieldId}`,
    onSuccess: (data, variables) => {
      updateTransactionDetailResponse(queryClient, {
        transactionId: variables.transactionId,
        updater: (old) => ({
          ...old,
          additionalFields: data.additionalFields,
        }),
      })
    },
  })
}
